// =============================================================================
// Inventory tag (barcode label) printing for the Zebra label printer.
// Faithful port of the legacy POS InventoryEditView.PrintBarcode: a Small or
// Large label carrying the item's scannable barcode (Code 128 of the `scan`
// number), description, and price — printed N times (quantity prompt).
// Printing goes through the browser to the installed Zebra Windows driver; each
// label is its own page sized to the label stock so copies land one-per-label.
// =============================================================================
import { code128Svg } from './barcode'
import { printHtml } from './print'
import { currency } from './format'

export type TagSize = 'small' | 'large'

export interface TagInfo {
  name: string
  price: number
  code: string            // human-readable product/inventory code (shown as text)
  scan: string            // value encoded in the barcode + register scans back
  taxonomy?: string       // genus/species/variety (large tag only)
  size?: string           // pot/plant size, e.g. "#3"
}

// Label stock dimensions (inches). Adjust here if the Zebra roll changes.
const DIMS: Record<TagSize, { w: number; h: number; module: number; barH: number }> = {
  small: { w: 2.25, h: 1.25, module: 1.4, barH: 38 },
  large: { w: 3.0, h: 2.0, module: 2.0, barH: 64 },
}

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

function labelHtml(info: TagInfo, size: TagSize): string {
  const d = DIMS[size]
  const svg = code128Svg(info.scan, { module: d.module, height: d.barH, quiet: 8 })
  if (size === 'small') {
    return `<div class="label">
      <div class="name">${esc(info.name)}</div>
      <div class="price">${currency(info.price)}</div>
      <div class="bc">${svg}</div>
      <div class="cap">${esc(info.scan)}</div>
    </div>`
  }
  return `<div class="label">
    <div class="name">${esc(info.name)}</div>
    ${info.taxonomy ? `<div class="tax">${esc(info.taxonomy)}</div>` : ''}
    ${info.size ? `<div class="sz">Size: ${esc(info.size)}</div>` : ''}
    <div class="price">${currency(info.price)}</div>
    <div class="bc">${svg}</div>
    <div class="cap">${esc(info.code)} &nbsp;·&nbsp; ${esc(info.scan)}</div>
  </div>`
}

// Shared label-sheet CSS for a given size (one label per page).
function labelCss(size: TagSize): string {
  const d = DIMS[size]
  return `
    @page { size: ${d.w}in ${d.h}in; margin: 0; }
    html, body { margin: 0; padding: 0; }
    .label {
      width: ${d.w}in; height: ${d.h}in;
      padding: 0.06in 0.08in;
      box-sizing: border-box;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      text-align: center; overflow: hidden;
      page-break-after: always; break-after: page;
      font-family: Arial, Helvetica, sans-serif; color: #000;
    }
    .label:last-child { page-break-after: auto; break-after: auto; }
    .name { font-weight: bold; font-size: ${size === 'small' ? 10 : 15}px; line-height: 1.05;
            max-height: ${size === 'small' ? 2.2 : 2.4}em; overflow: hidden; }
    .tax { font-style: italic; font-size: 11px; color: #222; }
    .sz { font-size: 11px; }
    .price { font-weight: bold; font-size: ${size === 'small' ? 16 : 24}px; margin: 1px 0; }
    .bc { width: 95%; }
    .bc svg { width: 100%; height: ${d.barH}px; display: block; }
    .cap { font-family: 'Courier New', monospace; font-size: ${size === 'small' ? 8 : 10}px; letter-spacing: 1px; }
  `
}

// Batch label printing (one label per item) — items lacking a scan number are
// skipped. Returns the number of labels queued.
export function printProductTagsBatch(items: TagInfo[], size: TagSize): number {
  const printable = items.filter(i => i.scan && /\S/.test(i.scan))
  if (printable.length === 0) {
    alert('None of the selected items have a scan number, so no scannable labels can be printed.')
    return 0
  }
  const body = printable.map(i => labelHtml(i, size)).join('')
  printHtml(`Labels — ${printable.length} item${printable.length === 1 ? '' : 's'}`, body, labelCss(size))
  return printable.length
}

export function printProductTags(info: TagInfo, size: TagSize, qty: number): void {
  const count = Math.max(1, Math.min(200, Math.floor(qty) || 1))
  if (!info.scan || !/\S/.test(info.scan)) {
    alert('This item has no scan number, so a scannable tag cannot be printed. Assign a scan number first.')
    return
  }
  const d = DIMS[size]
  const body = Array.from({ length: count }, () => labelHtml(info, size)).join('')
  const css = `
    @page { size: ${d.w}in ${d.h}in; margin: 0; }
    html, body { margin: 0; padding: 0; }
    .label {
      width: ${d.w}in; height: ${d.h}in;
      padding: 0.06in 0.08in;
      box-sizing: border-box;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      text-align: center; overflow: hidden;
      page-break-after: always; break-after: page;
      font-family: Arial, Helvetica, sans-serif; color: #000;
    }
    .label:last-child { page-break-after: auto; break-after: auto; }
    .name { font-weight: bold; font-size: ${size === 'small' ? 10 : 15}px; line-height: 1.05;
            max-height: ${size === 'small' ? 2.2 : 2.4}em; overflow: hidden; }
    .tax { font-style: italic; font-size: 11px; color: #222; }
    .sz { font-size: 11px; }
    .price { font-weight: bold; font-size: ${size === 'small' ? 16 : 24}px; margin: 1px 0; }
    .bc { width: 95%; }
    .bc svg { width: 100%; height: ${d.barH}px; display: block; }
    .cap { font-family: 'Courier New', monospace; font-size: ${size === 'small' ? 8 : 10}px; letter-spacing: 1px; }
  `
  printHtml(`Tags — ${info.name}`, body, css)
}
